import type { SessionLineItemOptions } from '@/types/stripe.types'
import type { IUserSubscription } from '@/models/interfaces'
import { DuplicateSubscriptionError } from '@/models/models'
import type { PfcSponsorshipEntitlementStatus } from './accountSettings.enums'
import * as v from '@/types/valibot.types'

// Core subscription data shape for Account Settings/Signup flows
export type AccountSubscriptionInfo = IUserSubscription

export const createDefaultAccountSubscriptionInfo =
  (): AccountSubscriptionInfo => ({
    isFreeTrialEligible: false,
    duplicateSubscriptionError: DuplicateSubscriptionError._0,
    highestTier: 0,
    hasSubscription: false,
    activeSubscriptions: [],
    suspendedSubscriptions: []
  })

export interface PaywallFeatureCardProps {
  tier: number
  icon: string
  header: string
  subHeader: string
  showWebOnly: boolean
  bullets: string[]
  price: string
  showMonthly: boolean
  colors: { primary: string; secondary: string }
  current: boolean
  savings: string
  manage: boolean
  loading: boolean
  switchBilling: boolean
  disabled: boolean
  selected: boolean
  unClickable: boolean
}

export interface CreateCustomerPortalSessionRequest {
  returnUrl: string
  priceId: string
  planLevel: string
  upgrade: boolean
}

export interface CustomerPortalSession {
  sessionUrl: string
}

export interface AccountSettingsLocale {
  save: string
  generalInfo: {
    tab: string
    tabMobile: string
    srText: string
    name: {
      label: string
      pageTitle: string
      firstName: string
      firstNameError: string
      lastName: string
      lastNameError: string
      cta: string
      successToast: string
    }
    email: {
      label: string
      pageTitle: string
      cta: string
      helperText: string
      successToast: string
      noEmailError: string
      invalidEmailError: string
      verify: {
        successTitle: string
        emailVerified: string
        emailVerifiedLogin: string
        signIn: string
        errorTitle: string
        errorMsg: string
      }
      modal: { title: string; lineOne: string; lineTwo: string; submit: string }
    }
    timezone: {
      label: string
      secondaryLabel: string
      pageTitle: string
      cta: string
      successToast: string
      updateError: string
    }
    phoneNumber: {
      label: string
      pageTitle: string
      verified: string
      unverified: string
      empty: string
      description: string
      hintText: string
      removeNumberCta: string
      cta: string
      removeModal: {
        button: string
        title: string
        content: string
        modalCta: string
        successToast: string
      }
      modal: {
        title: string
        description: string
        cta: string
        sendAnotherCode: string
        resentText: string
        successVerifyToast: string
        successUpdateToast: string
      }
    }
    mfa: {
      label: string
      enable: string
      description: string
      modal: {
        title: string
        description: string
        cta: string
        sendAnotherCode: string
        resentText: string
        helpText: string
        helpCenter: string
      }
      enabledToast: string
      disabledToast: string
    }
    matchedLabel: string
    coParentLabel: string
    matchedOnLabel: string
    accountCreatedLabel: string
  }
  planDetails: {
    tab: string
    tabMobile: string
    label: string
    subscriptionType: { storeSub: string; complimentary: string }
    upgradePlan: string
    managePlan: string
    startFreeTrial: string
    upgradeToStandard: string
    upgradeToPremium: string
    upgradeTo: string
    tryForFree: string
    getPlanForPrice: string
    freeTrialForTier: string
    seeComparison: string
    billingInfoHeader: string
    billingTypeLabel: string
    billingTypes: { monthly: string; annual: string }
    billingNextLabel: string
    cancel: string
    successToast: string
    planName: {
      free: string
      basic: string
      enhanced: string
      standard: string
      ultimate: string
      premium: string
      unknown: string
    }
    tiers: {
      free: string
      basic: string
      standard: string
      premium: string
      unknown: string
      essentials: string
      enhanced: string
      ultimate: string
    }
    multiSubModal: {
      title: string
      subList: string
      cancelText: string
      tpSub: string
    }
    appleSubModal: { title: string; content: string; cta: string }
    androidSubModal: { title: string; content: string; cta: string }
    premiumConfirmModal: {
      title: string
      content: string
      cta: string
      cancelCta: string
    }
    complimentaryPlanModal: { title: string; content: string; cta: string }
    card: {
      perAccount: string
      currentPlan: string
      freeTrial: string
      premium: { access: string; priceTagline: string; bullets: string[] }
      standard: { access: string; priceTagline: string; bullets: string[] }
      free: { access: string; priceTagline: string; bullets: string[] }
    }
    freeRecord: {
      pdfHeader: string
      printedHeader: string
      statusLabel: string
      statusAvailable: string
      redeemedOnLabel: string
      nextAvailableLabel: string
      statusUnavailable: string
    }
    paywall: {
      header: string
      tableHeader: { monthly: string; annual: string; save: string }
      free: {
        header: string
        subHeader: string
        webOnly: string
        bullets: string[]
        monthlyPrice: string
        annualPrice: string
        savingsMonthly: string
        savingsAnnual: string
      }
      currency: string
      usd: string
      perMonth: string
      perYear: string
      upgradePlan: string
      currentPlan: string
      managePlan: string
      essentials: {
        header: string
        subHeader: string
        bullets: string[]
        monthlyPrice: string
        annualPrice: string
        savingsMonthly: string
        savingsAnnual: string
      }
      enhanced: {
        header: string
        subHeader: string
        bullets: string[]
        monthlyPrice: string
        annualPrice: string
        savingsMonthly: string
        savingsAnnual: string
      }
      ultimate: {
        header: string
        subHeader: string
        bullets: string[]
        monthlyPrice: string
        annualPrice: string
        savingsMonthly: string
        savingsAnnual: string
      }
      needHelpDeciding: string
      seeFullPricingPlan: string
      pricingPlan: string
      promo: {
        titlePrice1: string
        bullet1Price1: string
        bullet2Price1: string
        bullet2UpfrontPrice1: string
        bullet3Price1: string
        bullet3YourAccountPrice1: string
        titlePrice2: string
        bullet1Price2: string
        bullet2Price2: string
        bullet3Price2: string
        cta: string
        disclaimer: string
      }
    }
  }
  calling: { tab: string; tabMobile: string }
  notifications: {
    tab: string
    email: {
      label: string
      description: string
      disabledSuccessToast: string
      enabledSuccessToast: string
    }
    text: {
      label: string
      description: string
      remainingBalance: string
      disabledSuccessToast: string
      enabledSuccessToast: string
      disableModal: {
        title: string
        content: string
        confirmCta: string
        cancelCta: string
      }
      unverifiedModal: { title: string; content: string; confirmCta: string }
    }
    newsletter: { label: string; description: string; cta: string }
  }
  password: {
    tab: string
    srText: string
    current: { label: string; emptyError: string; incorrectError: string }
    new: {
      label: string
      emptyError: string
      requirementsError: string
      sameError: string
    }
    confirm: { label: string; emptyError: string; noMatchError: string }
    successToast: string
    update: string
  }
  links: { tos: string; privacyPolicy: string }
  messaging: {
    tab: string
    tabMobile: string
    toggle: { label: string; description: string }
    sentimentToggle: { label: string; description: string }
  }
  calendar: {
    tab: string
    tabMobile: string
    header: string
    description: string
    learnMore: string
    provider: {
      connectCta: string
      disconnected: string
      deleted: string
      updated: string
      toast: { connected: string }
      modal: {
        connect: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
        trash: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
        scopes: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
        disconnected: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
        deleted: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
        connectIssue: {
          header: string
          content: string
          confirmCta: string
          cancelCta: string
        }
      }
    }
  }
}

export interface CreateCheckoutSessionRequest {
  lineItems: SessionLineItemOptions[]
  metadata: { [key: string]: string }
  successUrl: string
  cancelUrl: string
  allowPromotionCodes: boolean
}

export interface CreateCheckoutSessionRequest {
  lineItems: SessionLineItemOptions[]
  metadata: { [key: string]: string }
  successUrl: string
  cancelUrl: string
  allowPromotionCodes: boolean
}

export interface CheckoutSession {
  sessionId: string
  sessionUrl: string
}

export interface PayForCoparentCheckoutSessionResponse {
  sessionId: string | null
  sessionUrl: string | null
  errorCode: number
  errorMessage: string | null
  success: boolean
}

export interface AcceptPayForCoparentSponsorshipResponse {
  acceptedWhen: string
  entitlementStatus: PfcSponsorshipEntitlementStatus
}

export interface MinutesPlan extends SessionLineItemOptions {
  itemId: number
  appleProductId: string
  minutes?: number
  price: number
  priceApple: number
  disabled: boolean
  productId: string
}

export interface FetchShowSentimentSettingResponse {
  enabled: boolean
}

export interface UpdateShowSentimentSettingRequest {
  enabled: boolean
}

export type UpdateShowSentimentSettingResponse = void

export interface UpdatePaymentRequestSuggestionsRequest {
  enabled: boolean
}

export type UpdatePaymentRequestSuggestionsResponse = string

// Matches TP.Core.Web/DTO/UpdateDateTimeSettingsRequest.cs
// (UpdateDateTimeSettingsRequestV2), consumed by
// POST /api/myaccount/updatedatetimesettings with api-version 2.0-unified.
export interface UpdateDateTimeSettingsRequestV2 {
  dateFormatId: number
  ianaTimeZone: string
}

// Request for POST /api/myaccount/enablemfasms (shared by account settings and
// signup). An empty phoneNumber is valid: account settings sends '' when the
// user's phone is already verified.
export const EnableMfaSmsRequestSchema = v.object({ phoneNumber: v.string() })

export type EnableMfaSmsRequest = v.InferOutput<
  typeof EnableMfaSmsRequestSchema
>

// NOTE: the IANA zone list type (IanaTimeZoneListResponse) moved to
// @/composables/timezone/timeZone.types - it's shared with Signup, not account
// settings specific.
